import { MoreVertical, Paperclip, Link as LinkIcon, AlertCircle, Edit, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { templateSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { memo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { setActiveBulkView, setCurrentTemplate, setIsTemplateDropdownModalOpen } from '@common-reducers/CampaignReducer';
import { duplicateTemplateForCampaignsThunk } from '@common-reducers/CampaignThunks';
import { DBTemplateThunks, TEMPLATE_LIST_QUERY } from "@common-reducers/DBServiceThunks";
import { resetScheduledMessageEditor, loadTemplateData } from '@common/reducers/ScheduledMessageEditorReducer';
import { BsPencil, BsTrash } from 'react-icons/bs';
import { MdOutlineContentCopy } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useRouter } from '@common-reducers/hooks/use-router';
import { isExtensionContext } from '@common/services/detect-context';
import { newTemplatePath } from '@common/utils/campaign-routes';
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context';

type Props = {
    templateId: string;
    index: number;
};

function TemplateCard({ templateId, index }: Props) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const router = useRouter();
    const inExtension = isExtensionContext();
    // Non-null under the redesign shell → URL-drive the template editor instead
    // of flipping Redux / router.navigate in place. Null for legacy/extension.
    const redesignNav = useCampaignsRedesignNav();
    const template = useAppSelector((state) => templateSelectOneObjectByQuery(state.DBTemplateReducer, {
        $or: [
            { deleted: { $exists: false } },
            { deleted: false }
        ],
        _id: templateId,
    }));
    const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);
    const [isLoadingDuplicateTemplate, setIsLoadingDuplicateTemplate] = useState(false);
    const [open, setOpen] = useState(false);
    const [isMouseOver, setIsMouseOver] = useState<boolean>(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    if (!template) return null;

    const hasFiles = !!template?.asset;
    const hasLink = !!template?.linkPreview?.url && !!template?.sendLinkPreview;
    const hasFilesOrLinks = hasFiles || hasLink;
    const isEmptyMessage = !template?.message && !template?.isPoll && !template?.asset?.type?.includes('audio');

    const handleEditClick = async () => {
        setOpen(false);
        // Redesign: URL-drive the template editor; skip the legacy Redux flip +
        // router.navigate. The sub-editor route seeds the template from its id.
        if (redesignNav && template._id) {
            redesignNav.openTemplate(template._id);
            return;
        }
        await dispatch(resetScheduledMessageEditor());
        dispatch(setCurrentTemplate(template));
        dispatch(loadTemplateData(template));

        const fromManagement = activeBulkView === "campaignManagementView";
        if (fromManagement) {
            dispatch(setActiveBulkView("campaignNewTemplateViewFromManagement"));
        } else {
            dispatch(setActiveBulkView("campaignNewTemplateViewFromNewCampaignView"));
        }

        // Website: deep-link the edit URL so refresh / back-button work and the
        // editor is shareable. Extension keeps Redux-only behavior.
        //
        // EXCEPTION — the management context (campaigns redesign's Templates tab):
        // the management container (CampaignManagementView, mounted in-pane by
        // ManagementViewAdapter) already swaps to NewTemplateView for
        // `campaignNewTemplateViewFromManagement`, so the Redux flag alone opens
        // the editor IN-PANE — exactly like the audience-board row-click. Routing
        // to /new-template here would LEAVE the redesign route; mirror the
        // audience row's "management view stays Redux-only" rule and skip it.
        if (!inExtension && !fromManagement) {
            router.navigate(newTemplatePath(template._id));
        }
    };

    const handleDuplicateClick = async () => {
        setIsLoadingDuplicateTemplate(true);
        setOpen(false);
        await dispatch(duplicateTemplateForCampaignsThunk({ oldTemplateId: template?._id ?? '' }));
        await dispatch(DBTemplateThunks.find({
            deleted: false,
            ...TEMPLATE_LIST_QUERY,
        }));
        setIsLoadingDuplicateTemplate(false);
    };

    const handleDeleteClick = () => {
        setOpen(false);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        dispatch(DBTemplateThunks.patch({
            entity: {
                ...template,
                deleted: true,
            }
        }));
        setIsDeleteDialogOpen(false);
    };

    const handleTemplateRowClick = () => {
        // Redesign: a card click opens the template's own editor URL (skip BOTH
        // the legacy Redux flip AND the website router.navigate from handleEditClick).
        if (redesignNav && template._id) {
            redesignNav.openTemplate(template._id);
            return;
        }
        const { _id: _ignored, ...templateWithoutId } = template ?? {};
        dispatch(loadTemplateData(templateWithoutId));
        dispatch(setIsTemplateDropdownModalOpen(false));
    };

    return (
        <div
            className={cn(
                "bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6",
                "hover:shadow-md transition-all cursor-pointer"
            )}
            onMouseEnter={() => setIsMouseOver(true)}
            onMouseLeave={() => setIsMouseOver(false)}
            onClick={handleTemplateRowClick}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    {/* Template Name and File Badge */}
                    <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 truncate">
                            {template.name || t('templates.unnamed')}
                        </h2>
                        {hasFilesOrLinks && (
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded-full text-sm font-medium flex-shrink-0">
                                {hasFiles && <Paperclip className="w-3.5 h-3.5" />}
                                {hasLink && !hasFiles && <LinkIcon className="w-3.5 h-3.5" />}
                                {hasFiles ? t('campaign.file') : t('campaign.link')}
                            </span>
                        )}
                    </div>

                    {/* Template Description/Message */}
                    <div className="mb-4">
                        {template?.message && template.message !== '' && !template.isPoll && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">
                                {template.message}
                            </p>
                        )}
                        {template?.isPoll && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">
                                {t('campaign.poll', { question: template.poll?.question })}
                            </p>
                        )}
                        {template?.asset?.type?.includes('audio') && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">
                                {template.asset.name}
                            </p>
                        )}
                        {isEmptyMessage && (
                            <p className="text-slate-400 dark:text-slate-500 text-sm italic">
                                This Template has no message
                            </p>
                        )}
                    </div>
                </div>

                {/* Actions Menu */}
                <div className="flex items-center justify-end gap-3 ml-4 flex-shrink-0 w-12">
                    {isMouseOver && !(activeBulkView === "campaignManagementView") && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick();
                            }}
                            className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                        >
                            <Edit className="h-5 w-5" />
                        </Button>
                    )}
                    {activeBulkView === "campaignManagementView" && !isLoadingDuplicateTemplate && (
                        <DropdownMenu open={open} onOpenChange={setOpen}>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    // stopPropagation only — opening is handled by Radix.
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                                >
                                    <MoreVertical className="h-5 w-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                className={cn(
                                    "w-[245px] p-0",
                                    "bg-white dark:bg-slate-800",
                                    "border border-slate-200 dark:border-slate-700",
                                    "rounded-xl shadow-md"
                                )}
                                align="end"
                            >
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditClick();
                                    }}
                                    className="cursor-pointer"
                                >
                                    <BsPencil className="h-4 w-4 mr-2" />
                                    Edit template
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDuplicateClick();
                                    }}
                                    className="cursor-pointer"
                                >
                                    <MdOutlineContentCopy className="h-4 w-4 mr-2" />
                                    Duplicate template
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteClick();
                                    }}
                                    className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/20"
                                >
                                    <BsTrash className="h-4 w-4 mr-2" />
                                    Delete template
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    {activeBulkView === "campaignManagementView" && isLoadingDuplicateTemplate && (
                        <div className="px-3 flex justify-end items-center">
                            <Loader2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 animate-spin" />
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md p-6 sm:p-8">
                    <DialogHeader className="space-y-3">
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            {t('templates.deleteTemplate', { defaultValue: 'Delete template' })}?
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground">
                            {t('templates.deleteTemplateConfirmation', {
                                // The locale value interpolates {{name}}; an unnamed template used to
                                // fall back to "this template" only inside the English defaultValue,
                                // so once the key resolved the quotes would have come out empty.
                                name: template.name || t('templates.unnamed', { defaultValue: 'Unnamed Template' }),
                                defaultValue: `Are you sure you want to delete "${template.name || 'this template'}"? This action cannot be undone.`
                            })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('common.cancel', { defaultValue: 'Cancel' })}
                        </Button>
                        <Button variant="destructive" onClick={handleConfirmDelete}>
                            {t('common.delete', { defaultValue: 'Delete' })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default memo(TemplateCard);

